// MapSection.jsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { GoogleMap, Marker, InfoWindow } from "@react-google-maps/api";
import { Box, IconButton } from "@mui/material";
import CustomButton from "../../../../components/CustomButton";
import maparrow from "../../../../assets/maparrow.svg";
import CloseIcon from "@mui/icons-material/Close";
import { useTranslation } from "react-i18next";
import {
  getAttendanceStatusDotColor,
  getAttendanceStatusPinMarkerIcon,
} from "../../../../components/StatusDropdown";

const defaultCenter = { lat: 37.0902, lng: -95.7129 };
const defaultZoom = 4;
const focusedZoom = 13;

/** Map legend: action types + schedule classifications (colors match StatusDropdown badges). */
const MAP_LEGEND_STATUS_KEYS = [
  "CLOCK_IN",
  "EARLY_CLOCK_IN",
  "LATE_CLOCK_IN",
  "BREAK_START",
  "BREAK_END",
  "CLOCK_OUT",
  "EARLY_CLOCK_OUT",
  "LATE_CLOCK_OUT",
];

const SCHEDULE_CLASSIFICATION_KEYS = new Set([
  "EARLY_CLOCK_IN",
  "LATE_CLOCK_IN",
  "ON_TIME_CLOCK_IN",
  "EARLY_CLOCK_OUT",
  "LATE_CLOCK_OUT",
  "ON_TIME_CLOCK_OUT",
]);
/** Map marker color key — break actions use break colors; else classification or action. */
function getRecordStatusKey(record) {
  const action = record?.actionType || record?.action_type;
  if (action === "BACK_FROM_BREAK") return "BREAK_END";
  if (action === "BREAK_START" || action === "BREAK_END") return action;

  const classification =
    record?.scheduleStatus?.classification ?? record?.schedule_status?.classification;
  if (classification != null && String(classification).trim() !== "") {
    return String(classification).trim();
  }
  return action;
}

const MapSection = ({ records, selectedPunch, onClearSelection }) => {
  const { t } = useTranslation();
  const [isFullMapOpen, setIsFullMapOpen] = useState(false);
  const [activeMarkerId, setActiveMarkerId] = useState(null);
  const previewMapRef = useRef(null);
  const fullMapRef = useRef(null);

  // Format action types like CLOCK_IN → Clock In
  const formatAction = (action) => {
    if (!action) return "";

    return action
      .toLowerCase()
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  const visibleRecords = selectedPunch
    ? records.filter(
        (r) => r.id === selectedPunch.id || r.punchId === selectedPunch.punchId
      )
    : records;

  const markers = useMemo(
    () =>
      (visibleRecords || [])
        .map((r) => {
          const coords = r?.locationData?.gps?.coordinates;
          if (!coords?.lat || !coords?.lng) return null;
          const actionType = r.actionType || r.action_type;
          return {
            id: r.punchId || r.id,
            position: { lat: coords.lat, lng: coords.lng },
            actionType,
            statusKey: getRecordStatusKey(r),
            time: r.timeData?.formatted || r.occurred_at,
            locationText: r.locationData?.textDescription || "No location",
          };
        })
        .filter(Boolean),
    [visibleRecords]
  );
  const legendItems = useMemo(
    () =>
      MAP_LEGEND_STATUS_KEYS.map((statusKey) => ({
        statusKey,
        color: getAttendanceStatusDotColor(statusKey, statusKey),
        label: SCHEDULE_CLASSIFICATION_KEYS.has(statusKey)
          ? t(`attendance.issueClassification.${statusKey}`, {
              defaultValue: formatAction(statusKey),
            })
          : t(`attendance.actionType.${statusKey}`, {
              defaultValue: formatAction(statusKey),
            }),
      })),
    [t]
  );

  const getMarkerIcon = useCallback((actionType, statusKey) => {
    return getAttendanceStatusPinMarkerIcon(statusKey, actionType);
  }, []);

  const fitMapToMarkers = useCallback((map) => {
    if (!map || !window.google) return;

    if (markers.length === 0) {
      map.setCenter(defaultCenter);
      map.setZoom(defaultZoom);
      return;
    }

    if (markers.length === 1) {
      map.setCenter(markers[0].position);
      map.setZoom(focusedZoom);
      return;
    }

    const bounds = new window.google.maps.LatLngBounds();
    markers.forEach((marker) => bounds.extend(marker.position));
    map.fitBounds(bounds, 40);
  }, [markers]);

  useEffect(() => {
    fitMapToMarkers(previewMapRef.current);
    fitMapToMarkers(fullMapRef.current);
  }, [fitMapToMarkers, isFullMapOpen]);

  useEffect(() => {
    if (activeMarkerId && !markers.some((marker) => marker.id === activeMarkerId)) {
      setActiveMarkerId(null);
    }
  }, [markers, activeMarkerId]);

  return (
    <>
      <Box
        sx={{
          position: "relative",
          border: "1px solid #E4E6EF",
          borderRadius: 2,
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexWrap: "nowrap",
            gap: 1.25,
            alignItems: "center",
            borderBottom: "1px solid #E4E6EF",
            px: 1.5,
            py: 2,
            backgroundColor: "#fff",
            overflowX: "auto",
            whiteSpace: "nowrap",
            "&::-webkit-scrollbar": {
              display: "none",
            },
          }}
        >
          {legendItems.map((item) => (
            <Box
              key={item.statusKey}
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 0.5,
                fontSize: 13,
                color: "#4B5675",
                fontWeight: 500,
                lineHeight: 1,
                flexShrink: 0,
              }}
            >
              <Box
                sx={{
                  width: 14,
                  height: 14,
                  borderRadius: "50%",
                  backgroundColor: item.color,
                  flexShrink: 0,
                }}
              />
              {item.label}
            </Box>
          ))}
        </Box>

        <GoogleMap
          mapContainerStyle={{ height: "100%", width: "100%", minHeight: 260 }}
          center={markers[0]?.position || defaultCenter}
          zoom={markers.length > 0 ? focusedZoom : defaultZoom}
          options={{ streetViewControl: false, mapTypeControl: false }}
          onLoad={(map) => {
            previewMapRef.current = map;
            fitMapToMarkers(map);
          }}
        >
          {markers.map((marker) => (
            <Marker
              key={marker.id}
              position={marker.position}
              icon={getMarkerIcon(marker.actionType, marker.statusKey)}
              onClick={() => setActiveMarkerId(marker.id)}
            >
              {activeMarkerId === marker.id && (
                <InfoWindow onCloseClick={() => setActiveMarkerId(null)}>
                  <div>
                    <b>
                      {t("lastActivity")} :{" "}
                      {SCHEDULE_CLASSIFICATION_KEYS.has(marker.statusKey)
                        ? t(`attendance.issueClassification.${marker.statusKey}`, {
                            defaultValue: formatAction(marker.statusKey),
                          })
                        : t(`attendance.actionType.${marker.actionType}`, {
                            defaultValue: formatAction(marker.actionType),
                          })}
                    </b>
                    <br />
                    {marker.time}
                    <br />
                    {marker.locationText}
                  </div>
                </InfoWindow>
              )}
            </Marker>
          ))}
        </GoogleMap>

        {selectedPunch && (
          <IconButton
            onClick={onClearSelection}
            sx={{
              position: "absolute",
              top: 52,
              left: 12,
              zIndex: 1200,
              backgroundColor: "red",
              "&:hover": { backgroundColor: "red" },
            }}
          >
            <CloseIcon sx={{ color: "white" }} />
          </IconButton>
        )}

        <CustomButton
          onClick={() => setIsFullMapOpen(true)}
          label={
            <>
              {t("dashboard.viewFullMap")}
              <Box
                component="img"
                src={maparrow}
                alt="view icon"
                sx={{ ml: 1, width: 15, height: 13, objectFit: "contain" }}
              />
            </>
          }
          size="small"
          sx={{
            position: "absolute",
            top: 52,
            right: 12,
            py: 0.5,
            height: 28,
            zIndex: 1000,
          }}
        />
      </Box>

      {isFullMapOpen && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            zIndex: 2000,
            backgroundColor: "white",
          }}
        >
          <IconButton
            onClick={() => setIsFullMapOpen(false)}
            sx={{
              position: "absolute",
              top: 16,
              right: 16,
              zIndex: 2100,
              backgroundColor: "white",
              "&:hover": { backgroundColor: "white" },
            }}
          >
            <CloseIcon sx={{ color: "red" }} />
          </IconButton>

          <GoogleMap
            mapContainerStyle={{ height: "100%", width: "100%" }}
            center={markers[0]?.position || defaultCenter}
            zoom={markers.length > 0 ? focusedZoom : defaultZoom}
            options={{ streetViewControl: false, mapTypeControl: false }}
            onLoad={(map) => {
              fullMapRef.current = map;
              fitMapToMarkers(map);
            }}
          >
            {markers.map((marker) => (
              <Marker
                key={`full-${marker.id}`}
                position={marker.position}
                icon={getMarkerIcon(marker.actionType, marker.statusKey)}
                onClick={() => setActiveMarkerId(marker.id)}
              >
                {activeMarkerId === marker.id && (
                  <InfoWindow onCloseClick={() => setActiveMarkerId(null)}>
                    <div>
                      <b>
                        {SCHEDULE_CLASSIFICATION_KEYS.has(marker.statusKey)
                          ? t(`attendance.issueClassification.${marker.statusKey}`, {
                              defaultValue: formatAction(marker.statusKey),
                            })
                          : t(`attendance.actionType.${marker.actionType}`, {
                              defaultValue: formatAction(marker.actionType),
                            })}
                      </b>
                      <br />
                      {marker.time}
                      <br />
                      {marker.locationText}
                    </div>
                  </InfoWindow>
                )}
              </Marker>
            ))}
          </GoogleMap>
        </Box>
      )}
    </>
  );
};

export default MapSection;
